import React, { FC, useCallback, useEffect, useState } from "react";
import { styled } from "@mui/system";
import { COLORS, FONT_FAMILIES, FONT_WEIGHTS, GAPS, RADII, SPACINGS } from "../../common/consts";
import Button, { ButtonVariants } from "../Display/Button";
import Text, { TextVariant } from "../Display/Text";
import { BlockBone, PillBone, TextBone } from "../Display/Skeletons";
import { cancelAddon, getAddonStatus, startAddon } from "../../services/firebase";
import { AddonStatus, AddonStatusResult } from "../../types/addons";

const Grid = styled("div")`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: ${GAPS.md};
  max-width: 900px;
`;

const Card = styled("div")<{ active?: boolean }>`
  border: 1px solid ${({ active }) => (active ? `var(--color-borderBrandLight, ${COLORS.borderBrandLight})` : `var(--color-borderTertiary, ${COLORS.borderTertiary})`)};
  border-radius: ${RADII.md};
  background: var(--color-backgroundWhite, ${COLORS.backgroundWhite});
  padding: ${SPACINGS.lg};
  display: flex;
  flex-direction: column;
  gap: ${GAPS.sm};
`;

const Title = styled("div")`
  font-family: ${FONT_FAMILIES.titles};
  font-weight: ${FONT_WEIGHTS.semibold};
  font-size: 1rem;
  color: var(--color-foregroundPrimary, ${COLORS.foregroundPrimary});
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Pill = styled("span")<{ tone: "ok" | "muted" }>`
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  color: ${({ tone }) => (tone === "ok" ? "#fff" : `var(--color-foregroundSecondary, ${COLORS.foregroundSecondary})`)};
  background: ${({ tone }) => (tone === "ok" ? `var(--color-integrationConnected, ${COLORS.integrationConnected})` : `var(--color-backgroundSecondary, ${COLORS.backgroundSecondary})`)};
`;

const Muted = styled(Text)`
  color: var(--color-foregroundSecondary, ${COLORS.foregroundSecondary});
`;

const Price = styled("div")`
  font-family: ${FONT_FAMILIES.titles};
  font-weight: ${FONT_WEIGHTS.semibold};
  color: var(--color-foregroundPrimary, ${COLORS.foregroundPrimary});
`;

const Row = styled("div")`
  display: flex;
  gap: ${GAPS.sm};
  align-items: center;
  margin-top: auto;
`;

const money = (cents: number | null | undefined, currency = "usd") =>
  cents == null ? "" : new Intl.NumberFormat("en-US", { style: "currency", currency: currency.toUpperCase(), maximumFractionDigits: 0 }).format(cents / 100);

interface Props {
  onChanged?: (status: AddonStatusResult) => void;
}

/**
 * The add-on grid before the catalogue lands: a card per add-on, each with
 * its name and state pill, description, price, seat line and action.
 */
export const AddonsSectionSkeleton: FC = () => (
  <div style={{ display: "flex", flexDirection: "column", gap: GAPS.md }}>
    <Grid>
      {[0, 1, 2].map((i) => (
        <Card key={i}>
          <Title>
            <TextBone width={132} size="1rem" />
            <PillBone width={56} height={18} />
          </Title>
          <div>
            <TextBone width="100%" size="0.875rem" />
            <TextBone width="72%" size="0.875rem" />
          </div>
          <TextBone width={168} size="1rem" />
          <TextBone width="60%" size="0.875rem" />
          <Row>
            <BlockBone width={116} height={36} radius="9999px" />
          </Row>
        </Card>
      ))}
    </Grid>
    <Row style={{ marginTop: 0 }}>
      <TextBone width={220} size="0.875rem" />
      <BlockBone width={96} height={36} radius="9999px" />
      <BlockBone width={88} height={36} radius="9999px" />
    </Row>
  </div>
);

/** Self-serve add-ons: subscribe, cancel, and see who is assigned. Seats are assigned on the Users page. */
const AddonsSection: FC<Props> = ({ onChanged }) => {
  const [status, setStatus] = useState<AddonStatusResult | null>(null);
  const [interval, setInterval] = useState<"monthly" | "yearly">("monthly");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const s = await getAddonStatus();
      setStatus(s);
      const active = s.addons.find((a) => a.billing?.interval);
      if (active?.billing?.interval) setInterval(active.billing.interval);
    } catch (e) {
      setError("Could not load add-ons.");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (addon: AddonStatus, action: "start" | "cancel") => {
    setBusy(addon.key);
    setError(null);
    try {
      const next = action === "start" ? await startAddon(addon.key, interval) : await cancelAddon(addon.key);
      setStatus(next);
      onChanged?.(next);
    } catch (e: any) {
      setError(e?.message || "Something went wrong.");
    } finally {
      setBusy(null);
    }
  };

  if (!status && error) return <Muted variant={TextVariant.sm}>{error}</Muted>;

  if (!status) return <AddonsSectionSkeleton />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: GAPS.md }}>
      {!status.canSelfServe && status.reason && <Muted variant={TextVariant.sm}>{status.reason}</Muted>}
      {error && <Text variant={TextVariant.sm} style={{ color: `var(--color-integrationDisconnected, ${COLORS.integrationDisconnected})` }}>{error}</Text>}
      <Grid>
        {status.addons.map((addon) => {
          const price = addon.prices[interval];
          const perSeat = money(price?.unitAmount, price?.currency);
          const subscribed = addon.source === "subscription";
          return (
            <Card key={addon.key} active={addon.enabled}>
              <Title>
                {addon.name}
                <Pill tone={addon.enabled ? "ok" : "muted"}>
                  {addon.enabled ? (addon.source === "subscription" ? "Active" : "Included") : "Off"}
                </Pill>
              </Title>
              <Muted variant={TextVariant.sm}>{addon.description}</Muted>
              {addon.configured ? (
                <Price>
                  {perSeat} per user, {interval === "yearly" ? "per year" : "per month"}
                </Price>
              ) : (
                <Muted variant={TextVariant.sm}>Contact us to turn this on.</Muted>
              )}
              <Muted variant={TextVariant.sm}>
                {addon.enabled
                  ? addon.seatLimit === null
                    ? `${addon.assigned.length || "All licensed"} users`
                    : `${addon.assigned.length} of ${addon.seatLimit} seats assigned`
                  : "Assign users on the Users page once it is on."}
              </Muted>
              <Row>
                {subscribed ? (
                  <Button variant={ButtonVariants.light} type="button" disabled={busy === addon.key} loading={busy === addon.key} onClick={() => act(addon, "cancel")}>
                    Cancel add-on
                  </Button>
                ) : !addon.enabled ? (
                  <Button variant={ButtonVariants.brand} type="button" disabled={!status.canSelfServe || !addon.configured || busy === addon.key} loading={busy === addon.key} onClick={() => act(addon, "start")}>
                    Subscribe
                  </Button>
                ) : null}
              </Row>
            </Card>
          );
        })}
      </Grid>
      <Row style={{ marginTop: 0 }}>
        <Muted variant={TextVariant.sm} style={{ marginRight: "auto" }}>Billing interval for new add-ons</Muted>
        <Button variant={interval === "monthly" ? ButtonVariants.brand : ButtonVariants.light} type="button" onClick={() => setInterval("monthly")}>
          Monthly
        </Button>
        <Button variant={interval === "yearly" ? ButtonVariants.brand : ButtonVariants.light} type="button" onClick={() => setInterval("yearly")}>
          Yearly
        </Button>
      </Row>
    </div>
  );
};

export default AddonsSection;
